/**
 * The editor's own visual language.
 *
 * One place for every colour, radius and shadow the tool uses, because the two halves of it
 * are drawn by different machinery: the panels are MUI components and the plan is painted
 * onto a canvas. Without a shared set, a room is one blue in the layers list and a different
 * blue on the drawing — which reads as two unrelated things rather than one thing and its
 * switch.
 *
 * Deliberately not the app-wide `StyleConstants`. Those describe a dashboard of white cards
 * on a coloured page; this is a working surface, and a drawing tool needs a quiet neutral
 * shell so that the only saturated colour on screen belongs to the drawing.
 */

export const editorTheme = {
  /** The shell: rails, bars, popovers. */
  surface: '#FFFFFF',
  /** One step down, for the well a control sits in and for panel headers. */
  surfaceSunken: '#F7F8FA',
  /**
   * The drawing surface: white, edge to edge.
   *
   * There is no separate sheet under the plan any more. A tinted workspace with white paper on
   * it drew a rectangle around the *viewport's* idea of the plan's extent, which is not a
   * thing in the building — and it made the ruling invisible everywhere outside it. One white
   * surface with the grid across all of it is what a drawing tool actually looks like.
   */
  workspace: '#FFFFFF',

  line: '#DCE0E8',
  lineSoft: '#EDEFF4',

  ink: '#1B2230',
  inkDim: '#5A657A',
  inkFaint: '#8B95A8',

  /** The one saturated colour in the chrome: selection, active tool, focus. */
  accent: '#1F6FE0',
  accentSoft: '#E8F0FD',
  accentInk: '#FFFFFF',

  ok: '#0F9D58',
  warn: '#B06A00',
  danger: '#CF2E2E',

  radius: 8,
  radiusSmall: 6,

  /**
   * The keyboard focus ring, stated once.
   *
   * MUI's default outline disappears against the accent-coloured controls in this tool, and a
   * drawing tool is one people drive from the keyboard — losing the focus ring on the tool
   * rail means losing track of which tool Tab has reached.
   */
  focusRing: '0 0 0 2px #FFFFFF, 0 0 0 4px #1F6FE0',

  /** For anything floating over the drawing: the tool bar, the zoom bar, the minimap. */
  shadowFloating: '0 4px 16px rgba(20, 30, 50, 0.14)',
  shadowPanel: '0 1px 3px rgba(20, 30, 50, 0.08)',

  /** Numbers that are read against each other — zoom, areas, counts — line up in columns. */
  numeric: '"SF Mono", "Roboto Mono", ui-monospace, monospace',
} as const;

/**
 * What each layer is drawn in, on the canvas and in the panels alike.
 *
 * A floorplan is read as a stack: the plate, then the structure standing on it, then the
 * rooms tinted over that, then the furniture, then the seats. The ramp runs from quiet to
 * loud in that order, so the thing an admin is placing is always the most visible thing on
 * screen.
 */
export const layerPalette = {
  /** The slab a plan draws for itself on its floor layer, where it has one. */
  plate: '#FBFBFA',
  plateEdge: '#C8CCD4',

  /** The picture a plan was traced from, kept as drawn: ink, softened so the plan reads over it. */
  drawing: '#5B616B',

  /** Structure: solid enough to read as material at a glance. */
  wall: '#B9BFC9',
  wallEdge: '#6E7685',

  /** Rooms sit in front of the walls, so the fill is translucent and the outline is not. */
  room: '#CFE0F7',
  roomOpacity: 0.5,
  roomEdge: '#3F78C4',

  /** Furniture: warm and quiet, so it separates from the cool structure without shouting. */
  deskShape: '#E6E4DF',
  deskShapeEdge: '#B4AFA4',

  /**
   * A seat. The loudest thing on the plan, because it is what the whole tool is about.
   *
   * The same green the mobile app draws an available workplace in (`Styles.green`), so an
   * admin comparing the editor against the app on their phone sees one plan rather than two.
   * The edge is darker than the app's flat fill because these markers sit on white paper
   * rather than on the app's tinted floor.
   */
  desk: '#84C47F',
  deskEdge: '#4A8046',
  /** Drawn but not bookable: the one state worth interrupting for. */
  deskUnbookable: '#E8A33D',
  deskRetired: '#98A1B2',

  label: '#1B2230',

  /** Under the pointer. A ring rather than a colour swap, so nothing changes what it is. */
  hover: '#7FB0F5',
  /** In the selection. */
  selection: '#1F6FE0',
  selectionFill: 'rgba(31, 111, 224, 0.10)',
  /** A selected or hovered shape's fill: the accent, well diluted, so the shape still reads. */
  accentTint: '#DCE9FB',

  /** The shape being drawn, before it is anything. */
  draft: '#1F6FE0',

  // Dark enough to read on the workspace as well as on the sheet, which is what lets one pass
  // of the ruling cover the whole canvas instead of two clipped ones.
  gridMinor: '#DBE1EA',
  gridMajor: '#C6CEDA',
} as const;

/**
 * A panel's section heading.
 *
 * Small, spaced and uppercase, the way every drawing tool labels its panels. It is not a
 * style choice so much as a hierarchy one: the headings have to be legible without competing
 * with the controls under them, and at this size and weight they read as furniture rather
 * than as content.
 */
export const sectionHeadingSx = {
  fontSize: 11,
  fontWeight: 650,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  color: editorTheme.inkFaint,
};

/**
 * Anything that floats over the drawing: the tool rail, the view bar, the zoom bar, the
 * minimap.
 *
 * One definition rather than four copies. They sit on the same surface at the same height, so
 * a slab that is a shade lighter or a pixel less round than the one beside it reads as a
 * mistake — and four copies is how that happens.
 *
 * Frosted, so the drawing underneath stays faintly readable where a slab covers it, which
 * matters at the edges of a plan that fills the view.
 */
export const floatingSlabSx = {
  position: 'absolute' as const,
  zIndex: 2,
  borderRadius: `${editorTheme.radius}px`,
  backgroundColor: 'rgba(255, 255, 255, 0.92)',
  border: `1px solid ${editorTheme.line}`,
  boxShadow: editorTheme.shadowFloating,
  backdropFilter: 'blur(8px)',
};

export default editorTheme;

/**
 * What the image review outlines each kind of find in: one clear colour each, strong enough to
 * read over a drawing in grey, and never the accent, which marks the selection.
 */
export const reviewPalette = {
  desk: '#2E7D32',
  room: '#3F78C4',
  label: '#8E24AA',
  furniture: '#B06A00',
  wall: '#37474F',
  door: '#D81B60',
  window: '#00ACC1',
  stairs: '#6D4C41',
  column: '#546E7A',
  toilet: '#0277BD',
  sink: '#00838F',
  bathtub: '#1565C0',
  shower: '#4527A0',
  hob: '#BF360C',
} as const;
